import { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { Label } from '@shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select'
import { FormTextarea } from '@shared/components/forms/fields/FormTextarea'
import { DialogResultTransition, type DialogStatus } from '@shared/components/ui/DialogResultTransition'
import { cn } from '@shared/lib/utils'
import type { PaymentReviewFormData, PaymentReviewDecision } from '../types'

interface PaymentReviewFormProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: PaymentReviewFormData) => Promise<void> | void
  onSuccess?: () => void
  /** Payment amount display string, e.g. "$4,235.00" */
  paymentAmount?: string
  /** Why the payment is under review */
  reviewReason?: string
}

const APPROVE_REASONS = [
  { value: 'low_risk_confirmed',   label: 'Low risk confirmed' },
  { value: 'customer_verified',    label: 'Customer verified' },
  { value: 'manual_review_passed', label: 'Manual review passed' },
  { value: 'other',                label: 'Other' },
]

const DECLINE_REASONS = [
  { value: 'high_risk',           label: 'High risk' },
  { value: 'policy_violation',    label: 'Policy violation' },
  { value: 'suspected_fraud',     label: 'Suspected fraud' },
  { value: 'unverified_customer', label: 'Unverified customer' },
  { value: 'other',               label: 'Other' },
]

const DECISION_OPTIONS: Array<{ value: PaymentReviewDecision; label: string }> = [
  { value: 'approve', label: 'Approve payment' },
  { value: 'decline', label: 'Decline payment' },
]

const AUTO_CLOSE_MS = 1500

export function PaymentReviewForm({
  open,
  onClose,
  onSubmit,
  onSuccess,
  paymentAmount,
  reviewReason,
}: PaymentReviewFormProps) {
  const [decision, setDecision]   = useState<PaymentReviewDecision | ''>('')
  const [reason, setReason]       = useState('')
  const [notes, setNotes]         = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus]       = useState<DialogStatus>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    if (open) {
      setDecision('')
      setReason('')
      setNotes('')
      setIsLoading(false)
      setStatus('idle')
    }
    return () => clearTimeout(timerRef.current)
  }, [open])

  // Clear reason when decision changes
  useEffect(() => { setReason('') }, [decision])

  const isValid = (decision as string) !== '' && reason !== ''

  const currentReasons = decision === 'decline' ? DECLINE_REASONS : APPROVE_REASONS

  const handleSubmit = async () => {
    if (!isValid || isLoading || decision === '') return
    setIsLoading(true)
    try {
      await onSubmit({
        decision,
        reason,
        notes: notes.trim(),
      })
      setStatus('success')
      timerRef.current = setTimeout(() => {
        onSuccess?.()
        onClose()
      }, AUTO_CLOSE_MS)
    } catch {
      setStatus('error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && status !== 'success') onClose() }}>
      <DialogContent className="max-w-md">
        <DialogResultTransition
          status={status}
          successMessage={decision === 'approve' ? 'Payment approved' : 'Payment declined'}
          errorMessage="Failed to process decision"
          errorDescription="Something went wrong. Please try again."
          onRetry={() => setStatus('idle')}
          onClose={onClose}
        >
          <DialogHeader>
            <DialogTitle>Review payment</DialogTitle>
            <DialogDescription>
              Review the payment details and make a decision.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {(paymentAmount || reviewReason) && (
              <div className="rounded-lg bg-muted/50 p-4 space-y-2">
                {paymentAmount && (
                  <div className="space-y-0.5">
                    <div className="text-xs text-muted-foreground font-mono uppercase tracking-wide">Amount</div>
                    <div className="text-sm font-medium">{paymentAmount}</div>
                  </div>
                )}
                {reviewReason && (
                  <div className="space-y-0.5">
                    <div className="text-xs text-muted-foreground font-mono uppercase tracking-wide">Review reason</div>
                    <div className="text-sm font-medium">{reviewReason}</div>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Decision <span className="text-destructive">*</span>
              </Label>
              <Select value={decision} onValueChange={(v) => setDecision(v as PaymentReviewDecision)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select decision..." />
                </SelectTrigger>
                <SelectContent>
                  {DECISION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {decision !== '' && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Reason <span className="text-destructive">*</span>
                </Label>
                <div className="flex flex-wrap gap-2">
                  {currentReasons.map((r) => {
                    const isSelected = reason === r.value
                    return (
                      <button
                        key={r.value}
                        type="button"
                        onClick={() => setReason(r.value)}
                        className={cn(
                          'inline-flex items-center rounded-full px-3.5 py-1.5 text-[13px] font-medium border transition-colors',
                          isSelected
                            ? 'bg-foreground text-background border-foreground'
                            : 'bg-card text-foreground border-input hover:border-foreground/40',
                        )}
                        aria-pressed={isSelected}
                      >
                        {r.label}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            <FormTextarea
              label="Notes"
              description="Additional context for this action (optional)."
              value={notes}
              onChange={setNotes}
              placeholder="Add any additional context..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={onClose} className="font-medium">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!isValid || isLoading}
              className="rounded-full px-6"
            >
              {isLoading
                ? 'Processing...'
                : decision === 'approve'
                ? 'Approve payment'
                : decision === 'decline'
                ? 'Decline payment'
                : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogResultTransition>
      </DialogContent>
    </Dialog>
  )
}
